import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Inbox, RefreshCw, MailWarning, FileSpreadsheet, Settings2 } from "lucide-react";
import { adminLogin, adminLogout, adminMe, fetchStats, fetchSubmissions, fetchAdminSurveys, fetchAdminSettings, saveAdminSettings, resendEmails } from "../lib/api";
import { FadeIn, Kicker } from "../components/Reveal";
import SEO from "../components/SEO";

const inputCls = "w-full border border-border bg-background px-4 py-3 text-sm transition-colors placeholder:text-muted-foreground/60";
const BACKEND = process.env.REACT_APP_BACKEND_URL;

const FILTERS = [
  { key: "", label: "All" },
  { key: "join", label: "Join" },
  { key: "supporter", label: "WRD Supporters" },
  { key: "organizer", label: "Organizers" },
  { key: "contact", label: "Contact" },
];

const TYPE_BADGE = {
  join: "border-foliage/50 text-foliage dark:text-stalk",
  supporter: "border-gold/50 text-gold",
  organizer: "border-crimson/50 text-crimson dark:text-stalk",
  contact: "border-border text-muted-foreground",
};

const FIELD_LABELS = {
  farm_name: "Farm/Org", country: "Country", region: "Region", website: "Website",
  org_type: "Org type", varieties: "Varieties", cultivation_area: "Area", annual_production: "Production",
};

const SETTING_ROWS = [
  { key: "show_member_count", label: "Registered members count", desc: "Homepage hero shows the live number of verified registrations" },
  { key: "show_country_count", label: "Countries count", desc: "Homepage hero shows how many countries verified members come from" },
  { key: "show_directory", label: "Public database directory", desc: "Consented survey entries are listed publicly on the homepage" },
];

function LoginForm({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const res = await adminLogin(email, password);
      onLogin(res.data);
      toast.success("Signed in.");
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Sign-in failed. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto flex min-h-[70vh] max-w-md flex-col justify-center px-6 pt-24" data-testid="admin-login">
      <SEO page="home" noindex />
      <Kicker>WRA — Admin Inbox</Kicker>
      <h1 className="mt-6 font-serif text-3xl tracking-tight">Sign in</h1>
      <form onSubmit={onSubmit} className="corner-ticks mt-8 border border-border bg-card/40 p-8" data-testid="admin-login-form">
        <div className="space-y-4">
          <input required type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Admin email" className={inputCls} data-testid="admin-email-input" />
          <input required type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Password" className={inputCls} data-testid="admin-password-input" />
        </div>
        {error && <p className="mt-4 text-sm text-crimson dark:text-stalk" data-testid="admin-login-error">{error}</p>}
        <button type="submit" disabled={busy} data-testid="admin-login-submit-button" className="mt-6 w-full border border-crimson bg-crimson px-6 py-3.5 font-mono text-xs uppercase tracking-[0.25em] text-parchment transition-colors hover:bg-transparent hover:text-crimson disabled:opacity-60">
          {busy ? "Signing in…" : "Sign in"}
        </button>
      </form>
    </div>
  );
}

export default function Admin() {
  const [user, setUser] = useState(undefined);
  const [filter, setFilter] = useState("");
  const [subs, setSubs] = useState([]);
  const [surveys, setSurveys] = useState([]);
  const [stats, setStats] = useState(null);
  const [sheets, setSheets] = useState(null);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(null);
  const [params] = useSearchParams();

  useEffect(() => {
    adminMe().then((r) => setUser(r.data)).catch(() => setUser(null));
  }, []);

  useEffect(() => {
    if (params.get("sheets") === "connected") toast.success("Google Sheets connected.");
  }, [params]);

  const load = async (f = filter) => {
    setLoading(true);
    try {
      const [s, st, sv, sh, se] = await Promise.all([
        fetchSubmissions(f),
        fetchStats(),
        fetchAdminSurveys(),
        fetch(`${BACKEND}/api/admin/sheets-status`, { credentials: "include" }).then((r) => r.json()),
        fetchAdminSettings(),
      ]);
      setSubs(s.data);
      setStats(st.data);
      setSurveys(sv.data);
      setSheets(sh);
      setSettings(se.data);
    } catch {
      toast.error("Failed to load data.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user) load(filter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, filter]);

  if (user === undefined) return <div className="flex min-h-[60vh] items-center justify-center pt-24 font-mono text-xs uppercase tracking-[0.3em] text-muted-foreground">Loading…</div>;
  if (!user) return <LoginForm onLogin={setUser} />;

  const counts = stats?.expressions_of_interest || {};

  const onResend = async (s) => {
    setResending(s.id);
    try {
      await resendEmails(s.id);
      toast.success(`Emails re-sent for ${s.reference}`);
      await load();
    } catch {
      toast.error("Resend failed.");
    } finally {
      setResending(null);
    }
  };

  const onDisconnectSheets = async () => {
    try {
      await fetch(`${BACKEND}/api/oauth/sheets/disconnect`, { method: "POST", credentials: "include" });
      toast.success("Google Sheets disconnected.");
      await load();
    } catch {
      toast.error("Disconnect failed.");
    }
  };

  const onToggleSetting = async (key) => {
    const next = { ...settings, [key]: !settings[key] };
    setSettings(next);
    try {
      await saveAdminSettings(next);
      toast.success("Visibility updated.");
    } catch {
      toast.error("Failed to save setting.");
      await load();
    }
  };

  return (
    <main className="mx-auto max-w-7xl px-6 pt-32 pb-24 sm:px-10 lg:px-16" data-testid="admin-dashboard">
      <SEO page="home" noindex />
      <div className="flex flex-wrap items-center justify-between gap-6">
        <div>
          <Kicker>Admin — Submission Inbox</Kicker>
          <h1 className="mt-4 font-serif text-3xl tracking-tight sm:text-4xl">Expressions of interest</h1>
          <p className="mt-2 text-sm text-muted-foreground">Signed in as {user.email}</p>
        </div>
        <div className="flex gap-3">
          <button onClick={() => load()} data-testid="admin-refresh-button" className="flex items-center gap-2 border border-border px-4 py-2.5 font-mono text-[10px] uppercase tracking-[0.2em] transition-colors hover:border-crimson hover:text-crimson">
            <RefreshCw size={13} /> Refresh
          </button>
          <button
            onClick={async () => { await adminLogout(); setUser(null); }}
            data-testid="admin-logout-button"
            className="flex items-center gap-2 border border-crimson px-4 py-2.5 font-mono text-[10px] uppercase tracking-[0.2em] text-crimson transition-colors hover:bg-crimson hover:text-parchment"
          >
            <LogOut size={13} /> Sign out
          </button>
        </div>
      </div>

      <div className="mt-10 grid grid-cols-2 gap-px border border-border bg-border sm:grid-cols-4" data-testid="admin-stats">
        {[
          ["Join", counts.join || 0],
          ["WRD Supporters", counts.supporter || 0],
          ["Organizers", counts.organizer || 0],
          ["Contact", counts.contact || 0],
        ].map(([k, v]) => (
          <div key={k} className="bg-background px-5 py-4">
            <p className="font-serif text-3xl">{v}</p>
            <p className="mt-1 font-mono text-[9px] uppercase tracking-[0.2em] text-muted-foreground">{k}</p>
          </div>
        ))}
      </div>

      <FadeIn className="mt-8 border border-border bg-card/40" data-testid="visibility-settings-card">
        <div className="flex items-center gap-3 border-b border-border p-5">
          <Settings2 size={20} className="text-crimson dark:text-stalk" />
          <div>
            <p className="font-display text-sm font-semibold">Public visibility</p>
            <p className="mt-0.5 font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Choose what the homepage publishes — counters read the same live data as this inbox</p>
          </div>
        </div>
        {SETTING_ROWS.map((row) => (
          <div key={row.key} className="flex items-center justify-between gap-4 border-b border-border px-5 py-4 last:border-0" data-testid={`setting-${row.key}`}>
            <div>
              <p className="text-sm font-medium">{row.label}</p>
              <p className="mt-0.5 text-xs text-muted-foreground">{row.desc}</p>
            </div>
            <button
              onClick={() => onToggleSetting(row.key)}
              disabled={!settings}
              data-testid={`setting-toggle-${row.key}`}
              aria-pressed={settings?.[row.key]}
              className={`relative h-6 w-11 shrink-0 rounded-full transition-colors ${settings?.[row.key] ? "bg-foliage" : "bg-foreground/20"}`}
            >
              <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-parchment shadow transition-transform ${settings?.[row.key] ? "translate-x-[22px]" : "translate-x-0.5"}`} />
            </button>
          </div>
        ))}
      </FadeIn>

      <FadeIn className="mt-8 flex flex-wrap items-center justify-between gap-4 border border-border bg-card/40 p-5" data-testid="sheets-status-card">
        <div className="flex items-center gap-3">
          <FileSpreadsheet size={20} className="text-foliage dark:text-stalk" />
          <div>
            <p className="font-display text-sm font-semibold">Google Sheets sync</p>
            <p className="mt-0.5 font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground" data-testid="sheets-status-text">
              {!sheets ? "Checking…" : !sheets.configured ? "Not configured — set GOOGLE_CLIENT_ID / GOOGLE_CLIENT_SECRET / GOOGLE_SHEET_ID on the server" : sheets.connected ? "Connected — new registrations and surveys are appended automatically" : "Configured — not connected"}
            </p>
          </div>
        </div>
        {sheets?.configured && !sheets?.connected && (
          <a href={`${BACKEND}/api/oauth/sheets/login`} data-testid="sheets-connect-button" className="border border-foliage px-4 py-2 font-mono text-[10px] uppercase tracking-[0.2em] text-foliage transition-colors hover:bg-foliage hover:text-parchment dark:text-stalk dark:border-stalk dark:hover:bg-stalk dark:hover:text-night">
            Connect Google Sheets
          </a>
        )}
        {sheets?.connected && (
          <button onClick={onDisconnectSheets} data-testid="sheets-disconnect-button" className="border border-border px-4 py-2 font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground transition-colors hover:border-crimson hover:text-crimson">
            Disconnect
          </button>
        )}
      </FadeIn>

      <div className="mt-8 flex flex-wrap gap-2" data-testid="admin-filters">
        {FILTERS.map((f) => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            data-testid={`admin-filter-${f.key || "all"}`}
            className={`border px-4 py-2 font-mono text-[10px] uppercase tracking-[0.2em] transition-colors ${filter === f.key ? "border-crimson bg-crimson text-parchment" : "border-border text-muted-foreground hover:text-foreground"}`}
          >
            {f.label}
          </button>
        ))}
      </div>

      <FadeIn className="mt-6 overflow-x-auto border border-border" data-testid="admin-submissions-table">
        {subs.length === 0 ? (
          <div className="flex flex-col items-center gap-3 bg-background p-16 text-center">
            <Inbox size={28} className="text-muted-foreground" />
            <p className="font-mono text-xs uppercase tracking-[0.2em] text-muted-foreground" data-testid="admin-empty-state">{loading ? "Loading…" : "No submissions yet"}</p>
          </div>
        ) : (
          <table className="w-full min-w-[1080px] bg-background text-sm">
            <thead>
              <tr className="border-b border-border text-left font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                <th className="px-4 py-3.5">Reference</th>
                <th className="px-4 py-3.5">Type</th>
                <th className="px-4 py-3.5">Name</th>
                <th className="px-4 py-3.5">Email</th>
                <th className="px-4 py-3.5">Country / Region</th>
                <th className="px-4 py-3.5">Verified</th>
                <th className="px-4 py-3.5">Survey</th>
                <th className="px-4 py-3.5">Emails</th>
                <th className="px-4 py-3.5">Date</th>
              </tr>
            </thead>
            <tbody>
              {subs.map((s) => {
                const emailsOk = s.verified ? (s.registration_email_sent && s.admin_notification_sent) : s.verification_email_sent;
                return (
                  <tr key={s.id} className="border-b border-border last:border-0 hover:bg-card/60" data-testid={`submission-row-${s.reference}`}>
                    <td className="px-4 py-3.5 font-mono text-xs text-crimson dark:text-stalk">{s.reference}</td>
                    <td className="px-4 py-3.5"><span className={`border px-2 py-0.5 font-mono text-[9px] uppercase tracking-[0.15em] ${TYPE_BADGE[s.type] || TYPE_BADGE.contact}`}>{s.type}</span></td>
                    <td className="px-4 py-3.5 font-medium">{s.name}</td>
                    <td className="px-4 py-3.5 text-muted-foreground">{s.email}</td>
                    <td className="px-4 py-3.5">{[s.country, s.region].filter(Boolean).join(" / ") || "—"}</td>
                    <td className="px-4 py-3.5" data-testid={`verified-status-${s.reference}`}>
                      {s.verified
                        ? <span className="border border-foliage/50 px-2 py-0.5 font-mono text-[9px] uppercase tracking-[0.15em] text-foliage dark:text-stalk">Verified</span>
                        : <span className="border border-gold/50 px-2 py-0.5 font-mono text-[9px] uppercase tracking-[0.15em] text-gold">Pending</span>}
                    </td>
                    <td className="px-4 py-3.5" data-testid={`survey-status-${s.reference}`}>
                      {s.survey_answered
                        ? <span className="border border-foliage/50 px-2 py-0.5 font-mono text-[9px] uppercase tracking-[0.15em] text-foliage dark:text-stalk">Answered</span>
                        : <span className="font-mono text-[9px] uppercase tracking-[0.15em] text-muted-foreground">—</span>}
                    </td>
                    <td className="px-4 py-3.5" data-testid={`email-status-${s.reference}`}>
                      {emailsOk ? (
                        <span className="font-mono text-[9px] uppercase tracking-[0.15em] text-foliage dark:text-stalk">
                          {s.verified ? "Reg ✓ Admin ✓" : "Verify ✓"}
                        </span>
                      ) : (
                        <button
                          onClick={() => onResend(s)}
                          disabled={resending === s.id}
                          data-testid={`resend-emails-${s.reference}`}
                          className="flex items-center gap-1.5 border border-gold/50 px-2 py-0.5 font-mono text-[9px] uppercase tracking-[0.15em] text-gold transition-colors hover:bg-gold hover:text-ink disabled:opacity-60"
                        >
                          <MailWarning size={11} />
                          {resending === s.id ? "Sending…" : "Resend"}
                        </button>
                      )}
                    </td>
                    <td className="px-4 py-3.5 font-mono text-xs text-muted-foreground">{new Date(s.created_at).toLocaleDateString()}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </FadeIn>
      <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
        Verify = confirmation email · Reg = welcome email · Admin = owner notification · Personal data visible to admins only.
      </p>

      <div className="mt-16">
        <Kicker>Global Rhubarb Database — Survey Answers</Kicker>
        <FadeIn className="mt-6 overflow-x-auto border border-border" data-testid="admin-surveys-table">
          {surveys.length === 0 ? (
            <div className="flex flex-col items-center gap-3 bg-background p-12 text-center">
              <p className="font-mono text-xs uppercase tracking-[0.2em] text-muted-foreground" data-testid="admin-surveys-empty">No survey answers yet</p>
            </div>
          ) : (
            <table className="w-full min-w-[860px] bg-background text-sm">
              <thead>
                <tr className="border-b border-border text-left font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                  <th className="px-4 py-3.5">Reference</th>
                  <th className="px-4 py-3.5">Farm / Org</th>
                  <th className="px-4 py-3.5">Country / Region</th>
                  <th className="px-4 py-3.5">Public consent</th>
                  <th className="px-4 py-3.5">Fields allowed</th>
                  <th className="px-4 py-3.5">Updated</th>
                </tr>
              </thead>
              <tbody>
                {surveys.map((sv) => (
                  <tr key={sv.id} className="border-b border-border last:border-0 hover:bg-card/60" data-testid={`survey-row-${sv.reference}`}>
                    <td className="px-4 py-3.5 font-mono text-xs text-crimson dark:text-stalk">{sv.reference}</td>
                    <td className="px-4 py-3.5 font-medium">{sv.farm_name}</td>
                    <td className="px-4 py-3.5">{[sv.country, sv.region].filter(Boolean).join(" / ")}</td>
                    <td className="px-4 py-3.5">
                      {sv.publish_consent
                        ? <span className="border border-foliage/50 px-2 py-0.5 font-mono text-[9px] uppercase tracking-[0.15em] text-foliage dark:text-stalk">Yes</span>
                        : <span className="border border-border px-2 py-0.5 font-mono text-[9px] uppercase tracking-[0.15em] text-muted-foreground">No</span>}
                    </td>
                    <td className="px-4 py-3.5 text-xs text-muted-foreground">
                      {sv.publish_consent ? (sv.publish_fields || []).map((f) => FIELD_LABELS[f] || f).join(", ") || "—" : "Not public"}
                    </td>
                    <td className="px-4 py-3.5 font-mono text-xs text-muted-foreground">{new Date(sv.updated_at).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </FadeIn>
      </div>
    </main>
  );
}
